import React, { useEffect, useState } from 'react';

import { SvgIcon } from '@material-ui/core';

import { close, down } from '@/assets/icons';
import { AsaphDropdownMenu } from '@/components/shared/AsaphDropdownMenu';
import { Chip } from '@/components/shared/Chip';
import { RoundedButton } from '@/components/shared/RoundedButton';
import { classNames } from '@/helpers/classNames';

import styles from './SetlistsFilterBar.module.scss';

type MultiSelectChipProps = {
    label: string;
    options: { label: string; value: string }[];
    selected: string[];
    onToggle: (value: string) => void;
};

const MultiSelectChip: React.FC<MultiSelectChipProps> = ({
    label,
    options,
    selected,
    onToggle,
}) => {
    const [anchorEl, setAnchorEl] = useState<Element | null>(null);

    useEffect(() => {
        if (!anchorEl) return;
        const t = setTimeout(() => window.dispatchEvent(new Event('resize')), 210);
        return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    const isActive = selected.length > 0;
    const displayLabel = isActive ? `${label}: ${selected.length}` : label;

    const chevron = <SvgIcon component={down} style={{ fontSize: 14 }} />;

    const panel = (
        <div className={styles['multi-select-panel']}>
            {options.length === 0 ? (
                <div className={styles['multi-select-empty']}>No options</div>
            ) : (
                options.map((opt) => (
                    <div
                        key={opt.value}
                        className={styles['multi-select-row']}
                        onClick={() => onToggle(opt.value)}
                    >
                        <input
                            type="checkbox"
                            readOnly
                            checked={selected.includes(opt.value)}
                        />
                        <span>{opt.label}</span>
                    </div>
                ))
            )}
        </div>
    );

    return (
        <>
            <Chip
                variant="rounded"
                selected={isActive}
                className={isActive ? styles['chip--active'] : styles['chip--default']}
                trailingComponent={chevron}
                onClick={(e) => setAnchorEl(e.currentTarget)}
            >
                {displayLabel}
            </Chip>
            <AsaphDropdownMenu
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                items={[]}
                customContent={panel}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            />
        </>
    );
};

export type SetlistsFilterBarProps = {
    serviceFilter: string[];
    onServiceToggle: (value: string) => void;
    /** Omit to hide the chip entirely */
    serviceOptions?: { label: string; value: string }[];
    creatorFilter: string[];
    onCreatorToggle: (value: string) => void;
    /** Omit to hide the chip entirely */
    creatorOptions?: { label: string; value: string }[];
    onClearAll: () => void;
};

export const SetlistsFilterBar: React.FC<SetlistsFilterBarProps> = ({
    serviceFilter,
    onServiceToggle,
    serviceOptions,
    creatorFilter,
    onCreatorToggle,
    creatorOptions,
    onClearAll,
}) => {
    const hasActiveFilters = serviceFilter.length > 0 || creatorFilter.length > 0;

    const xIcon = <SvgIcon component={close} style={{ fontSize: 12 }} />;

    if (!serviceOptions && !creatorOptions && !hasActiveFilters) {
        return null;
    }

    return (
        <div className={styles['filter-bar']}>
            <div
                className={classNames([
                    styles['clear-button-wrapper'],
                    hasActiveFilters && styles['clear-button-wrapper--visible'],
                ])}
            >
                <RoundedButton color="default" leadingComponent={xIcon} onClick={onClearAll}>
                    Clear
                </RoundedButton>
            </div>
            {serviceOptions && (
                <MultiSelectChip
                    label="Service"
                    options={serviceOptions}
                    selected={serviceFilter}
                    onToggle={onServiceToggle}
                />
            )}
            {creatorOptions && (
                <MultiSelectChip
                    label="Creator"
                    options={creatorOptions}
                    selected={creatorFilter}
                    onToggle={onCreatorToggle}
                />
            )}
        </div>
    );
};
